/**
 * FeedbackWidget — floating feedback button (bottom-left corner).
 *
 * Features:
 * - 4 feedback types: BUG, IMPROVEMENT, QUESTION, FEEDBACK
 * - Optional screenshot capture via html2canvas (lazy-imported)
 * - Annotation overlay for marking up screenshots
 * - Auto-captures page URL, page title, console log, and last stack trace
 */
import React, { useRef, useState, useEffect, useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { getLogBuffer, getLastStackTrace } from "@/lib/logCapture";

// ─── Constants ───────────────────────────────────────────────────────────────

const TYPES = [
  { key: "BUG", label: "Bug", emoji: "🐛", color: "bg-red-50 text-red-700 border-red-200 hover:bg-red-100" },
  { key: "IMPROVEMENT", label: "Improvement", emoji: "✨", color: "bg-violet-50 text-violet-700 border-violet-200 hover:bg-violet-100" },
  { key: "QUESTION", label: "Question", emoji: "❓", color: "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100" },
  { key: "FEEDBACK", label: "Feedback", emoji: "💬", color: "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100" },
];

const PLACEHOLDERS = {
  BUG: "Describe what happened and what you expected…",
  IMPROVEMENT: "Describe the improvement you have in mind…",
  QUESTION: "What would you like to know…",
  FEEDBACK: "Share your thoughts…",
};

// ─── Screenshot helpers ───────────────────────────────────────────────────────

async function captureScreen(containerRef) {
  const { default: html2canvas } = await import("html2canvas");
  const canvas = await html2canvas(document.body, {
    useCORS: true,
    scale: Math.min(window.devicePixelRatio, 2),
    logging: false,
  });
  const maxW = 1280;
  let w = canvas.width;
  let h = canvas.height;
  if (w > maxW) {
    h = Math.round((h * maxW) / w);
    w = maxW;
  }
  const out = document.createElement("canvas");
  out.width = w;
  out.height = h;
  out.getContext("2d").drawImage(canvas, 0, 0, w, h);
  return out.toDataURL("image/jpeg", 0.72);
}

/** Bake annotations onto screenshot and return new dataURL. */
function bakeAnnotations(screenshotDataUrl, annotations, imgW, imgH) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      ctx.strokeStyle = "#f97316";
      ctx.lineWidth = 3;
      ctx.fillStyle = "rgba(249,115,22,0.15)";
      for (const ann of annotations) {
        const x = (ann.x / 100) * img.width;
        const y = (ann.y / 100) * img.height;
        const w = (ann.width / 100) * img.width;
        const h = (ann.height / 100) * img.height;
        ctx.fillRect(x, y, w, h);
        ctx.strokeRect(x, y, w, h);
      }
      resolve(canvas.toDataURL("image/jpeg", 0.9));
    };
    img.src = screenshotDataUrl;
  });
}

// ─── Annotation Overlay ───────────────────────────────────────────────────────

function AnnotationOverlay({ screenshot, annotations, onChange, onDone }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(null); // { x, y } start point (%)
  const [imgSize, setImgSize] = useState({ w: 1, h: 1 });

  // Handle escape key
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onDone(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onDone]);

  const containerRef = useRef(null);

  function pct(e) {
    const rect = containerRef.current.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    };
  }

  function onMouseDown(e) {
    e.preventDefault();
    const p = pct(e);
    // Check if clicking an existing annotation to remove it
    for (let i = 0; i < annotations.length; i++) {
      const a = annotations[i];
      if (
        p.x >= a.x && p.x <= a.x + a.width &&
        p.y >= a.y && p.y <= a.y + a.height
      ) {
        onChange(annotations.filter((_, idx) => idx !== i));
        return;
      }
    }
    setDrawing(p);
  }

  function onMouseMove(e) {
    if (!drawing) return;
    e.preventDefault();
    const p = pct(e);
    // Will be rendered in the preview rects below
    containerRef.current.dataset.curX = p.x;
    containerRef.current.dataset.curY = p.y;
    // Force re-render for live rect preview
    setDrawing((d) => ({ ...d, _cur: p }));
  }

  function onMouseUp(e) {
    if (!drawing) return;
    const p = pct(e);
    const x = Math.min(drawing.x, p.x);
    const y = Math.min(drawing.y, p.y);
    const w = Math.abs(p.x - drawing.x);
    const h = Math.abs(p.y - drawing.y);
    if (w > 1 && h > 1) {
      onChange([...annotations, { x, y, width: w, height: h }]);
    }
    setDrawing(null);
  }

  const liveRect =
    drawing && drawing._cur
      ? {
          x: Math.min(drawing.x, drawing._cur.x),
          y: Math.min(drawing.y, drawing._cur.y),
          width: Math.abs(drawing._cur.x - drawing.x),
          height: Math.abs(drawing._cur.y - drawing.y),
        }
      : null;

  return (
    <div
      className="fixed inset-0 z-[200] flex flex-col"
      style={{ background: "rgba(0,0,0,0.85)" }}
    >
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-3 bg-zinc-900/90 backdrop-blur border-b border-zinc-700 shrink-0">
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-white">Annotate screenshot</span>
          {annotations.length > 0 && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-orange-500 text-white text-xs font-semibold">
              {annotations.length}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 text-xs text-zinc-400">
          <span>Click existing box to remove · Drag to add</span>
          <button
            onClick={onDone}
            className="ml-4 px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white font-medium text-sm"
          >
            Done
          </button>
        </div>
      </div>

      {/* Canvas area */}
      <div className="flex-1 flex items-center justify-center p-4 overflow-hidden">
        <div
          ref={containerRef}
          className="relative select-none"
          style={{
            backgroundImage: `url(${screenshot})`,
            backgroundSize: "contain",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            width: "100%",
            height: "100%",
            cursor: "crosshair",
          }}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
        >
          {/* Saved annotations */}
          {annotations.map((a, i) => (
            <div
              key={i}
              style={{
                position: "absolute",
                left: `${a.x}%`,
                top: `${a.y}%`,
                width: `${a.width}%`,
                height: `${a.height}%`,
                border: "2px solid #f97316",
                background: "rgba(249,115,22,0.15)",
                boxSizing: "border-box",
                cursor: "pointer",
              }}
            />
          ))}
          {/* Live drawing rect */}
          {liveRect && (
            <div
              style={{
                position: "absolute",
                left: `${liveRect.x}%`,
                top: `${liveRect.y}%`,
                width: `${liveRect.width}%`,
                height: `${liveRect.height}%`,
                border: "2px dashed #f97316",
                background: "rgba(249,115,22,0.1)",
                boxSizing: "border-box",
                pointerEvents: "none",
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Widget ──────────────────────────────────────────────────────────────

export default function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("FEEDBACK");
  const [description, setDescription] = useState("");
  const [screenshot, setScreenshot] = useState(null); // dataURL
  const [annotations, setAnnotations] = useState([]);
  const [annotating, setAnnotating] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [success, setSuccess] = useState(false);
  const widgetRef = useRef(null);

  // Close on escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === "Escape" && !annotating) setOpen(false); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, annotating]);

  // Auto-close after success
  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => {
      setOpen(false);
      setTimeout(resetForm, 400);
    }, 2200);
    return () => clearTimeout(t);
  }, [success]);

  function resetForm() {
    setType("FEEDBACK");
    setDescription("");
    setScreenshot(null);
    setAnnotations([]);
    setSuccess(false);
  }

  const mutation = useMutation({
    mutationFn: async (payload) => {
      const { data } = await api.post("/feedback", payload);
      return data;
    },
    onSuccess: () => setSuccess(true),
  });

  async function handleScreenshot() {
    setCapturing(true);
    // Hide widget for one paint cycle
    if (widgetRef.current) widgetRef.current.style.visibility = "hidden";
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    try {
      const dataUrl = await captureScreen();
      setScreenshot(dataUrl);
      setAnnotations([]);
    } catch (err) {
      console.error("Screenshot failed:", err);
    } finally {
      if (widgetRef.current) widgetRef.current.style.visibility = "visible";
      setCapturing(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    let finalScreenshot = screenshot;
    if (screenshot && annotations.length > 0) {
      finalScreenshot = await bakeAnnotations(screenshot, annotations);
    }
    mutation.mutate({
      type,
      description,
      pageUrl: window.location.pathname,
      pageTitle: document.title,
      screenshot: finalScreenshot || undefined,
      annotations: annotations.length > 0 ? annotations : undefined,
      consoleLog: getLogBuffer() || undefined,
      stackTrace: getLastStackTrace() || undefined,
    });
  }

  if (annotating && screenshot) {
    return (
      <AnnotationOverlay
        screenshot={screenshot}
        annotations={annotations}
        onChange={setAnnotations}
        onDone={() => setAnnotating(false)}
      />
    );
  }

  return (
    <div ref={widgetRef} className="fixed bottom-5 left-5 z-50 flex flex-col items-start gap-2">
      {/* Panel */}
      {open && (
        <div
          className="w-80 sm:w-96 rounded-2xl shadow-2xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 overflow-hidden"
          style={{
            animation: "feedbackSlideUp 0.35s cubic-bezier(0.22,1,0.36,1) both",
          }}
        >
          {success ? (
            /* Success state */
            <div className="flex flex-col items-center justify-center py-12 px-6 gap-3">
              <span className="text-5xl">✅</span>
              <p className="text-base font-semibold text-zinc-800 dark:text-zinc-100">Thank you!</p>
              <p className="text-sm text-zinc-500 text-center">Your feedback has been submitted.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col">
              {/* Header */}
              <div className="px-4 pt-4 pb-2 border-b border-zinc-100 dark:border-zinc-800 flex items-center justify-between">
                <span className="text-sm font-semibold text-zinc-700 dark:text-zinc-200">Send feedback</span>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 text-lg leading-none w-7 h-7 flex items-center justify-center rounded"
                >
                  ✕
                </button>
              </div>

              <div className="p-4 flex flex-col gap-3">
                {/* Type selector */}
                <div className="grid grid-cols-4 gap-1.5">
                  {TYPES.map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => setType(t.key)}
                      className={`flex flex-col items-center gap-0.5 py-2 px-1 rounded-lg border text-xs font-medium transition-all ${
                        type === t.key
                          ? t.color + " ring-2 ring-offset-1 ring-current"
                          : "border-zinc-200 dark:border-zinc-700 text-zinc-500 hover:bg-zinc-50 dark:hover:bg-zinc-800"
                      }`}
                    >
                      <span className="text-base">{t.emoji}</span>
                      <span>{t.label}</span>
                    </button>
                  ))}
                </div>

                {/* Description */}
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={PLACEHOLDERS[type]}
                  rows={4}
                  className="w-full rounded-lg border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800 text-sm text-zinc-800 dark:text-zinc-100 px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-emerald-400 placeholder:text-zinc-400"
                />

                {/* Screenshot tools */}
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleScreenshot}
                    disabled={capturing}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-zinc-200 dark:border-zinc-700 text-xs font-medium text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 disabled:opacity-50"
                  >
                    📷 {capturing ? "Capturing…" : "Screenshot"}
                  </button>
                  {screenshot && (
                    <button
                      type="button"
                      onClick={() => setAnnotating(true)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-violet-200 bg-violet-50 text-xs font-medium text-violet-700 hover:bg-violet-100"
                    >
                      ✏️ Annotate{annotations.length > 0 ? ` (${annotations.length})` : ""}
                    </button>
                  )}
                </div>

                {/* Thumbnail */}
                {screenshot && (
                  <div className="relative h-24 rounded-lg overflow-hidden border border-zinc-200 dark:border-zinc-700 group">
                    <img src={screenshot} alt="Screenshot" className="w-full h-full object-cover object-top" />
                    <button
                      type="button"
                      onClick={() => { setScreenshot(null); setAnnotations([]); }}
                      className="absolute top-1 right-1 w-6 h-6 rounded-full bg-zinc-900/70 text-white text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      ×
                    </button>
                  </div>
                )}

                {/* Submit row */}
                <div className="flex items-center justify-between gap-2 pt-1">
                  <span className="text-[11px] text-zinc-400 truncate flex-1">{window.location.pathname}</span>
                  <button
                    type="submit"
                    disabled={description.trim().length < 5 || mutation.isPending}
                    className="shrink-0 px-4 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white text-sm font-semibold transition-colors"
                  >
                    {mutation.isPending ? "Sending…" : "Send"}
                  </button>
                </div>

                {mutation.isError && (
                  <p className="text-xs text-red-600">
                    Failed to send — please try again.
                  </p>
                )}
              </div>
            </form>
          )}
        </div>
      )}

      {/* Trigger button */}
      <button
        type="button"
        onClick={() => {
          if (open) { setOpen(false); } else { resetForm(); setOpen(true); }
        }}
        className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold shadow-lg hover:shadow-xl transition-all"
        aria-label={open ? "Close feedback" : "Give feedback"}
      >
        {open ? "✕ Close" : "💬 Feedback"}
      </button>

      {/* Slide-up animation */}
      <style>{`
        @keyframes feedbackSlideUp {
          from { opacity: 0; transform: translateY(16px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0)    scale(1);    }
        }
      `}</style>
    </div>
  );
}
